/**
 * Field Component Registry
 *
 * Maps each FieldType to its per-context Vue components and exposes
 * `getComponent(context, field)` for resolution. Each entry is a
 * `FieldComponents` map from `FieldContext` → component.
 *
 * One component per context — `field.isArray` flows through as a prop and
 * the component branches internally on single-vs-array.
 *
 * Filter is distinct from form: filter pickers must be safe for live use
 * (no save/cancel UX, no mutation side effects). A type gets a `filter`
 * entry only if it has a safe live picker; otherwise the filter UI falls
 * back to a native input.
 *
 * Resolution walks the FieldType chain leaf → root (e.g. currency → number
 * → value) and returns the first registered component for the requested
 * context. Leaf types that override (markdown, currency, event, …) declare
 * their own entry; those without one inherit their parent's component. For
 * link/record/type fields, `field.recordType` flows through as a prop to
 * scope the picker.
 *
 * The registry is open. This package registers the renderers it owns
 * (`registerDefaultFieldComponents`, in ./defaults); an app registers the rest
 * with `registerFieldComponents` at startup — the renderers that need the app's
 * stores or clients. Registrations live here (not on each FieldType file) so
 * core's `types/<kind>.ts` files stay pure data. Without the split, the import
 * chain forms a cycle: types.ts → types/value.ts → StringInput.vue →
 * useField.ts → fields.ts → types.ts.
 */

import type { Component } from "vue"
import {
  type Field,
  FIELD_TYPES,
  type FieldType,
  type FieldTypeName,
  getFieldType,
  type RootType,
} from "@auger/core/fields"
import StatusSelectField from "./value/StatusSelectField.vue"

/** The UI contexts a field renders in. `cell` is the in-grid renderer (an
 *  XXXField, hosted by GridCell); `form` is the standalone input — the filter
 *  builder uses it too, enriching the field so a record-less picker resolves. */
export type FieldContext = "cell" | "form"

/** Per-context component map for a FieldType. */
export type FieldComponents = Partial<Record<FieldContext, Component>>

export type FieldComponentMap = Partial<Record<RootType | FieldTypeName, FieldComponents>>

// Root defaults sit at the top; concrete types inherit via the parent walk
// in getComponent and only need an entry when they override.
const COMPONENT_REGISTRY: FieldComponentMap = {}

/**
 * Add components to the registry. Entries merge per type and per context, so a
 * later call can add a type's `cell` without disturbing its `form`; registering
 * the same type and context again replaces it.
 */
export function registerFieldComponents(components: FieldComponentMap): void {
  for (const [name, byContext] of Object.entries(components) as [
    RootType | FieldTypeName,
    FieldComponents,
  ][]) {
    COMPONENT_REGISTRY[name] = { ...COMPONENT_REGISTRY[name], ...byContext }
  }
}

/**
 * Resolve the component for a field in a given UI context by walking the type
 * chain (leaf → parent → …) for the first registered match. Returns null when
 * the chain registers none (no component → render nothing); test that to detect
 * absence.
 *
 * Resolution depends only on `type`, so the parameter is a structural subset
 * of `Field` — callers resolving from a partial field descriptor (group
 * cells, type-only lookups) don't need a full instance.
 */
export function getComponent(context: FieldContext, field: Pick<Field, "type">): Component | null {
  let current: FieldType | undefined = getFieldType(field.type)
  while (current) {
    const match = COMPONENT_REGISTRY[current.name]?.[context]
    if (match) return match
    current = current.parent ? FIELD_TYPES[current.parent] : undefined
  }
  return null
}

/**
 * Whether a status field renders as a GLYPH — the difference that decides
 * whether one can sit in a 22px slot beside a name, or in a block's title bar.
 *
 * Asks the registry rather than the type: `task_status`, `review_status` and
 * `deal_status` resolve to StatusSelectField, which collapses to a bare mark
 * when given no room. A plain `select` has only its label, and pinned narrow it
 * clips "Scheduled" to "S".
 */
export function isGlyphStatus(field?: Pick<Field, "type"> | null): boolean {
  return !!field?.type && getComponent("cell", field) === StatusSelectField
}
